import { useState } from 'react'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import { AiExtractedProduct } from './useAiProductsApi'

interface AiProductPreviewProps {
    data: AiExtractedProduct
    onChange: (data: AiExtractedProduct) => void
}

const PRODUCT_TYPE_LABELS: Record<string, string> = {
    kolonie: 'Kolonie i obozy',
    polkolonie: 'Półkolonie',
    'wycieczka-szkolna': 'Wycieczka szkolna',
    'zielona-szkola': 'Zielona szkoła',
    'noclegi-dla-grup': 'Noclegi dla grup'
}

const PRODUCT_TYPES = [
    { value: 'kolonie', label: 'Kolonie i obozy' },
    { value: 'polkolonie', label: 'Półkolonie' },
    { value: 'wycieczka-szkolna', label: 'Wycieczka szkolna' },
    { value: 'zielona-szkola', label: 'Zielona szkoła' },
    { value: 'noclegi-dla-grup', label: 'Noclegi dla grup' }
]

const FOOD_TYPE_LABELS: Record<string, string> = {
    '3-posilki': '3 posiłki dziennie',
    'pelne-wyzywienie': 'Pełne wyżywienie',
    sniadanie: 'Tylko śniadanie',
    'bez-wyzywienia': 'Bez wyżywienia',
    wlasne: 'Wyżywienie we własnym zakresie'
}

const TRANSPORT_TYPE_LABELS: Record<string, string> = {
    wlasny: 'Transport własny',
    zorganizowany: 'Transport zorganizowany',
    mieszany: 'Transport mieszany'
}

const INSURANCE_TYPE_LABELS: Record<string, string> = {
    'w-cenie': 'Ubezpieczenie w cenie',
    dodatkowe: 'Za dodatkową opłatą',
    brak: 'Brak ubezpieczenia'
}

function formatPrice(grosz?: number): string {
    if (!grosz && grosz !== 0) return '-'
    return `${(grosz / 100).toFixed(2)} zł`
}

function formatDate(dateStr?: string): string {
    if (!dateStr) return '-'
    try {
        return new Date(dateStr).toLocaleDateString('pl-PL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        })
    } catch {
        return dateStr
    }
}

/**
 * Preview & edit component for AI-extracted product data.
 * Displays all extracted fields in collapsible sections.
 * Each field can be inline-edited.
 */
export const AiProductPreview = ({ data, onChange }: AiProductPreviewProps) => {
    return (
        <div className="space-y-4">
            {/* Basic info */}
            <PreviewSection title="Informacje podstawowe" defaultOpen>
                <EditableField
                    label="Nazwa"
                    value={data.name}
                    onSave={val => onChange({ ...data, name: val })}
                />
                <div className="grid grid-cols-2 gap-4">
                    <SelectField
                        label="Typ produktu"
                        value={data.type}
                        options={PRODUCT_TYPES}
                        onChange={val =>
                            onChange({
                                ...data,
                                type: val as AiExtractedProduct['type']
                            })
                        }
                    />
                    <EditableField
                        label="Czas trwania (dni)"
                        value={data.duration_days?.toString() ?? ''}
                        onSave={val =>
                            onChange({ ...data, duration_days: val ? Number(val) : undefined })
                        }
                    />
                </div>
                <EditableField
                    label="Opis"
                    value={data.description}
                    multiline
                    onSave={val => onChange({ ...data, description: val })}
                />
                <div className="mt-2">
                    <span className="text-sm font-semibold text-gray-600">
                        Kategorie:
                    </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {data.categories?.map(cat => (
                            <Chip key={cat} label={cat} size="small" color="primary" variant="outlined" />
                        ))}
                        {(!data.categories || data.categories.length === 0) && (
                            <span className="text-gray-400 text-sm">Brak kategorii</span>
                        )}
                    </div>
                </div>
                {data.tags && data.tags.length > 0 && (
                    <div className="mt-2">
                        <span className="text-sm font-semibold text-gray-600">Tagi:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                            {data.tags.map(tag => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" />
                            ))}
                        </div>
                    </div>
                )}
            </PreviewSection>

            {/* Age & participants */}
            <PreviewSection title="Uczestnicy">
                <div className="grid grid-cols-2 gap-4">
                    <EditableField
                        label="Minimalny wiek"
                        value={data.min_age?.toString() ?? ''}
                        onSave={val =>
                            onChange({ ...data, min_age: val ? Number(val) : undefined })
                        }
                    />
                    <EditableField
                        label="Maksymalny wiek"
                        value={data.max_age?.toString() ?? ''}
                        onSave={val =>
                            onChange({ ...data, max_age: val ? Number(val) : undefined })
                        }
                    />
                </div>
            </PreviewSection>

            {/* Location */}
            <PreviewSection title="Lokalizacja">
                <div className="grid grid-cols-2 gap-4">
                    <ReadonlyField
                        label="Kraj"
                        value={
                            data.country_name
                                ? `${data.country_name} (${data.country_code})`
                                : data.country_code ?? '-'
                        }
                    />
                    <EditableField
                        label="Nazwa lokalizacji"
                        value={data.location_name ?? ''}
                        onSave={val => onChange({ ...data, location_name: val || undefined })}
                    />
                </div>
                {data.location && (
                    <div className="grid grid-cols-2 gap-4 mt-2">
                        <ReadonlyField
                            label="Województwo"
                            value={data.location.province ?? '-'}
                        />
                        <ReadonlyField
                            label="Miasto"
                            value={data.location.city ?? '-'}
                        />
                        {data.location.address && (
                            <ReadonlyField label="Adres" value={data.location.address} />
                        )}
                    </div>
                )}
                {data.location_types && data.location_types.length > 0 && (
                    <div className="mt-2">
                        <span className="text-sm font-semibold text-gray-600">
                            Typ lokalizacji:
                        </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                            {data.location_types.map(lt => (
                                <Chip key={lt} label={lt} size="small" variant="outlined" />
                            ))}
                        </div>
                    </div>
                )}
            </PreviewSection>

            {/* Transport */}
            {(data.transport_type || data.transport_description) && (
                <PreviewSection title="Transport">
                    <ReadonlyField
                        label="Typ"
                        value={
                            data.transport_type
                                ? TRANSPORT_TYPE_LABELS[data.transport_type] ?? data.transport_type
                                : '-'
                        }
                    />
                    {data.transport_description && (
                        <EditableField
                            label="Opis transportu"
                            value={data.transport_description}
                            multiline
                            onSave={val =>
                                onChange({ ...data, transport_description: val || undefined })
                            }
                        />
                    )}
                </PreviewSection>
            )}

            {/* Food */}
            {(data.food_type || data.food_description) && (
                <PreviewSection title="Wyżywienie">
                    <ReadonlyField
                        label="Typ"
                        value={
                            data.food_type
                                ? FOOD_TYPE_LABELS[data.food_type] ?? data.food_type
                                : '-'
                        }
                    />
                    {data.food_description && (
                        <EditableField
                            label="Opis wyżywienia"
                            value={data.food_description}
                            multiline
                            onSave={val =>
                                onChange({ ...data, food_description: val || undefined })
                            }
                        />
                    )}
                    <div className="flex gap-4 mt-1">
                        {data.food_vegetarian && (
                            <Chip label="Opcja wegetariańska" size="small" color="success" />
                        )}
                        {data.food_vegan && (
                            <Chip label="Opcja wegańska" size="small" color="success" />
                        )}
                    </div>
                </PreviewSection>
            )}

            {/* Insurance */}
            {(data.insurance_type || data.insurance_description) && (
                <PreviewSection title="Ubezpieczenie">
                    <ReadonlyField
                        label="Typ"
                        value={
                            data.insurance_type
                                ? INSURANCE_TYPE_LABELS[data.insurance_type] ?? data.insurance_type
                                : '-'
                        }
                    />
                    {data.insurance_description && (
                        <ReadonlyField label="Opis" value={data.insurance_description} />
                    )}
                </PreviewSection>
            )}

            {/* Schedule */}
            {(data.schedule_description || (data.schedule && data.schedule.length > 0)) && (
                <PreviewSection title="Plan wydarzenia / Harmonogram">
                    {data.schedule_description && (
                        <EditableField
                            label="Opis programu"
                            value={data.schedule_description}
                            multiline
                            onSave={val =>
                                onChange({ ...data, schedule_description: val || undefined })
                            }
                        />
                    )}
                    {data.schedule && data.schedule.length > 0 && (
                        <div className="mt-2 space-y-2">
                            {data.schedule.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="bg-gray-50 rounded p-3 border border-gray-200"
                                >
                                    <div className="font-semibold text-sm">
                                        Dzień {item.day}: {item.title}
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">
                                        {item.description}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </PreviewSection>
            )}

            {/* Conditions */}
            {(data.participation_conditions ||
                data.conditions_text_short ||
                data.conditions_text_long) && (
                    <PreviewSection title="Warunki uczestnictwa">
                        {data.participation_conditions && (
                            <ReadonlyField
                                label="Warunki"
                                value={data.participation_conditions}
                            />
                        )}
                        {data.conditions_text_short && (
                            <ReadonlyField
                                label="Warunki (krótko)"
                                value={data.conditions_text_short}
                            />
                        )}
                        {data.conditions_text_long && (
                            <ReadonlyField
                                label="Warunki (pełne)"
                                value={data.conditions_text_long}
                            />
                        )}
                    </PreviewSection>
                )}

            {/* Pricing */}
            <PreviewSection title="Cennik">
                <div className="grid grid-cols-2 gap-4">
                    <ReadonlyField
                        label="Cena minimalna"
                        value={formatPrice(data.min_price)}
                    />
                </div>
                {data.price_description && (
                    <EditableField
                        label="Opis ceny"
                        value={data.price_description}
                        multiline
                        onSave={val =>
                            onChange({ ...data, price_description: val || undefined })
                        }
                    />
                )}
                {data.price_includes_text && (
                    <ReadonlyField label="Cena zawiera" value={data.price_includes_text} />
                )}
                {data.price_not_includes_text && (
                    <ReadonlyField
                        label="Cena nie zawiera"
                        value={data.price_not_includes_text}
                    />
                )}
                {data.discounts_description && (
                    <ReadonlyField label="Zniżki" value={data.discounts_description} />
                )}
            </PreviewSection>

            {/* Trips / Terms */}
            {data.trips && data.trips.length > 0 && (
                <PreviewSection title={`Terminy (${data.trips.length})`} defaultOpen>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="text-left p-2 border">#</th>
                                    <th className="text-left p-2 border">Nazwa</th>
                                    <th className="text-left p-2 border">Start</th>
                                    <th className="text-left p-2 border">Koniec</th>
                                    <th className="text-right p-2 border">Cena</th>
                                    <th className="text-right p-2 border">Zaliczka</th>
                                    <th className="text-right p-2 border">Miejsca</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.trips.map((trip, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="p-2 border">{idx + 1}</td>
                                        <td className="p-2 border">{trip.name || '-'}</td>
                                        <td className="p-2 border">{formatDate(trip.start_date)}</td>
                                        <td className="p-2 border">{formatDate(trip.end_date)}</td>
                                        <td className="p-2 border text-right">
                                            {formatPrice(trip.full_price)}
                                        </td>
                                        <td className="p-2 border text-right">
                                            {formatPrice(trip.advance_price)}
                                        </td>
                                        <td className="p-2 border text-right">
                                            {trip.available_spots ?? '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </PreviewSection>
            )}

            {/* Additional info */}
            {data.additional_info_text && (
                <PreviewSection title="Dodatkowe informacje">
                    <ReadonlyField label="" value={data.additional_info_text} />
                </PreviewSection>
            )}
        </div>
    )
}

// ─── Helper components ────────────────────────────────────────

interface PreviewSectionProps {
    title: string
    defaultOpen?: boolean
    children: React.ReactNode
}

const PreviewSection = ({
    title,
    defaultOpen = false,
    children
}: PreviewSectionProps) => {
    const [open, setOpen] = useState(defaultOpen)

    return (
        <div className="bg-white rounded-lg shadow border">
            <button
                type="button"
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                onClick={() => setOpen(!open)}
            >
                <h3 className="text-base font-semibold">{title}</h3>
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </button>
            {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
        </div>
    )
}

interface EditableFieldProps {
    label: string
    value: string
    multiline?: boolean
    onSave: (val: string) => void
}

const EditableField = ({
    label,
    value,
    multiline,
    onSave
}: EditableFieldProps) => {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState(value)

    const handleSave = () => {
        onSave(draft)
        setEditing(false)
    }

    const handleStartEdit = () => {
        setDraft(value)
        setEditing(true)
    }

    if (editing) {
        return (
            <div>
                {label && (
                    <label className="text-sm font-semibold text-gray-600">
                        {label}
                    </label>
                )}
                <div className="flex gap-2 items-start mt-1">
                    <TextField
                        size="small"
                        fullWidth
                        multiline={multiline}
                        minRows={multiline ? 3 : 1}
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        autoFocus
                    />
                    <IconButton color="success" onClick={handleSave} size="small">
                        <CheckIcon />
                    </IconButton>
                </div>
            </div>
        )
    }

    return (
        <div>
            {label && (
                <label className="text-sm font-semibold text-gray-600">{label}</label>
            )}
            <div className="flex items-start gap-2 group">
                <div
                    className={`text-sm mt-1 flex-1 ${multiline ? 'whitespace-pre-wrap' : ''
                        }`}
                >
                    {value || '-'}
                </div>
                <IconButton
                    size="small"
                    onClick={handleStartEdit}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            </div>
        </div>
    )
}

interface ReadonlyFieldProps {
    label: string
    value: string
}

const ReadonlyField = ({ label, value }: ReadonlyFieldProps) => (
    <div>
        {label && (
            <label className="text-sm font-semibold text-gray-600">{label}</label>
        )}
        <div className="text-sm mt-1">{value || '-'}</div>
    </div>
)

interface SelectFieldProps {
    label: string
    value: string
    options: { value: string; label: string }[]
    onChange: (val: string) => void
}

const SelectField = ({ label, value, options, onChange }: SelectFieldProps) => (
    <div>
        <label className="text-sm font-semibold text-gray-600">{label}</label>
        <select
            value={value}
            onChange={e => onChange(e.target.value)}
            className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
        >
            {options.map(opt => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
    </div>
)
